import clsx from 'clsx';
import type { CSSProperties, FunctionComponent, ReactNode, Ref } from 'react';

import {
    StyledScrollArea,
    StyledScrollAreaCorner,
    StyledScrollAreaScrollbar,
    StyledScrollAreaThumb,
    StyledScrollAreaViewport,
} from '../../core/scroll-area/ScrollArea';
import { Tile } from '../../core/tile/Tile';

export type Content = {
    readonly id: number | string;
    readonly content: ReactNode;
    readonly flex?: number;
    readonly minWidth?: string;
    readonly visible?: boolean;
    readonly width: string;
};

export type BoardProps = {
    readonly content: readonly Content[];
};

export const Sidebar: FunctionComponent<{ readonly children: ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => <div className={`bg-surface-raised rounded-lg h-full p-m ${className ?? ''}`}>{children}</div>;

// https://github.com/radix-ui/primitives/issues/2964
// fix for display: table
/** The board's horizontal scroll area: columns 8px in from the left, snapping, with the thin board scrollbar. */
export const CroppedScrollArea: FunctionComponent<{
    readonly children: ReactNode;
    readonly style?: CSSProperties;
    readonly viewportRef?: Ref<HTMLDivElement>;
}> = ({ children, style, viewportRef }) => (
    <StyledScrollArea style={style}>
        <StyledScrollAreaViewport
            ref={viewportRef}
            className="scroll-area-viewport"
            style={{ height: '100%', scrollSnapType: 'x mandatory' }}
        >
            <div
                className="flex flex-row gap-xxl min-w-full w-fit [&>*:last-child]:pr-m"
                style={{ height: '100%', paddingLeft: 8 }}
            >
                {children}
            </div>
        </StyledScrollAreaViewport>
        <StyledScrollAreaScrollbar
            orientation="horizontal"
            style={{ bottom: '1px', left: 8, right: 8, width: 'calc(100% - 16px)' }}
        >
            <StyledScrollAreaThumb />
        </StyledScrollAreaScrollbar>
        <StyledScrollAreaCorner />
    </StyledScrollArea>
);

export const SPRING_CONFIG = {
    damping: 40,
    mass: 0.8,
    stiffness: 400,
    type: 'spring',
} as const;

export const BoardContentWithHeader: FunctionComponent<{
    readonly content: ReactNode;
    readonly header?: ReactNode;
    readonly headerPaddingRight?: boolean;
    readonly id?: string;
    readonly pageHeading: ReactNode;
}> = ({ content, header, headerPaddingRight = true, id, pageHeading }) => {
    return (
        <section className="flex h-full min-h-0 min-w-0 flex-col gap-group pt-m">
            {pageHeading}
            <Tile
                className="border border-border-subtle min-h-0 flex-1 p-m [&_.scroll-area-viewport>div]:!block [&_.scroll-area-viewport>div]:h-full px-0"
                id={id}
            >
                {header === undefined || header === null ? null : (
                    <div
                        className={clsx(
                            'flex min-w-0 items-center bg-surface-raised rounded-xl pl-m pt-0 w-full',
                            headerPaddingRight && 'pr-m',
                        )}
                    >
                        {header}
                    </div>
                )}
                <CroppedScrollArea style={{ flex: 1, minHeight: 0 }}>{content}</CroppedScrollArea>
            </Tile>
        </section>
    );
};
